'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface Marker {
  id: string;
  x: number;
  y: number;
  label: string;
  description: string;
}

const SAMPLE_DATA = {
  title: '1959 Gibson Les Paul Standard',
  year: '1959',
  category: 'Vintage Guitar',
  description: 'An exceptionally rare 1959 Gibson Les Paul Standard in original sunburst finish. This instrument represents the pinnacle of electric guitar design, with its lightweight mahogany body, figured maple top, and PAF humbucking pickups. The guitar has been professionally maintained and retains all original components.',
  collectorNotes: 'Acquired from the estate of a session musician who played on numerous Motown recordings in the 1960s. The wear patterns tell the story of countless studio sessions and live performances.',
  signedBy: ['Eric Clapton', 'Jimmy Page'],
  images: [
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_19aa54d73-1765753346482.png", alt: 'Front view of 1959 Les Paul' },
  { url: "https://images.unsplash.com/photo-1702859963843-246255e6a65f", alt: 'Headstock detail' },
  { url: "https://images.unsplash.com/photo-1695506976351-d7aa68bb7d67", alt: 'Back view showing grain' }],

  authenticity: {
    authenticated: true,
    inPerson: true,
    signed: true,
    certified: true,
    certifiedBy: 'Gruhn Guitars',
    certificateNumber: 'GG-LP-1959-0847',
    certificationDate: 'March 15, 2023'
  },
  markers: [
  { id: 'm1', x: 45, y: 25, label: '🎸', description: 'Original PAF pickup - Patent Applied For humbucker' },
  { id: 'm2', x: 50, y: 15, label: '🔧', description: 'Kluson tuners - All original from 1959' },
  { id: 'm3', x: 48, y: 45, label: '✨', description: 'Flame maple top - AAA grade figuring' },
  { id: 'm4', x: 52, y: 60, label: '🎚️', description: 'Original potentiometers dated 1959' },
  { id: 'm5', x: 46, y: 75, label: '🔒', description: 'Trapeze tailpiece - Factory original' },
  { id: 'm6', x: 55, y: 35, label: '📝', description: 'Serial number stamped in cavity' }]

};

export default function BoldEditorialViewer() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showMarkers, setShowMarkers] = useState(false);
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Bold Header */}
      <header className="border-b border-gray-800 py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[#ff0080] text-sm font-black uppercase tracking-[0.3em] mb-2">
                {SAMPLE_DATA.category}
              </div>
              <h1 className="text-6xl md:text-8xl font-black uppercase leading-none tracking-tight">
                {SAMPLE_DATA.title.split(' ').slice(0, 3).join(' ')}
              </h1>
              <h1 className="text-6xl md:text-8xl font-black uppercase leading-none tracking-tight text-[#00ffff]">
                {SAMPLE_DATA.title.split(' ').slice(3).join(' ')}
              </h1>
            </div>
            <div className="text-6xl font-black text-[#ffff00]">
              {SAMPLE_DATA.year}
            </div>
          </div>
        </div>
      </header>

      {/* Dynamic Grid Layout */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Image Section - 60% Width */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12">
          <div className="lg:col-span-3 relative group">
            <div className="aspect-[4/5] relative overflow-hidden">
              <Image
                src={SAMPLE_DATA.images[currentImageIndex].url}
                alt={SAMPLE_DATA.images[currentImageIndex].alt}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-105" />
              
              
              {/* Neon Markers */}
              {showMarkers && SAMPLE_DATA.markers.map((marker, idx) =>
              <div
                key={marker.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300 hover:scale-150 z-10 animate-pulse"
                style={{
                  left: `${marker.x}%`,
                  top: `${marker.y}%`,
                  animationDelay: `${idx * 0.15}s`
                }}
                onMouseEnter={() => setHoveredMarker(marker.id)}
                onMouseLeave={() => setHoveredMarker(null)}>
                
                  <div className="relative">
                    <div className="w-12 h-12 bg-[#ff0080] rounded-full flex items-center justify-center text-white shadow-[0_0_30px_#ff0080] border-2 border-white">
                      <span className="text-xl font-bold">{marker.label}</span>
                    </div>
                    {/* Glow Effect */}
                    <div className="absolute inset-0 bg-[#ff0080] rounded-full blur-xl opacity-50 animate-pulse"></div>
                  </div>
                  
                  {/* Neon Tooltip */}
                  {hoveredMarker === marker.id &&
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-4 w-64 bg-black border-2 border-[#00ffff] rounded-lg p-4 shadow-[0_0_30px_#00ffff] z-20">
                      <div className="text-[#00ffff] font-black text-sm uppercase mb-2">
                        {marker.label} {marker.description.split('-')[0]}
                      </div>
                      <div className="text-white text-xs">{marker.description.split('-')[1]}</div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="w-3 h-3 bg-[#00ffff] rotate-45"></div>
                      </div>
                    </div>
                }
                </div>
              )}
            </div>

            {/* Thumbnail Strip */}
            <div className="flex gap-4 mt-6">
              {SAMPLE_DATA.images.map((img, idx) =>
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`flex-1 aspect-square overflow-hidden transition-all duration-300 ${
                currentImageIndex === idx ?
                'ring-4 ring-[#ff0080] shadow-[0_0_20px_#ff0080]' :
                'opacity-50 hover:opacity-100'}`
                }>
                
                  <Image
                  src={img.url}
                  alt={img.alt}
                  width={150}
                  height={150}
                  className="w-full h-full object-cover" />
                
                </button>
              )}
            </div>
          </div>

          {/* Right Column - Authenticity & Quick Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Corner Ribbon Badges */}
            <div className="space-y-4">
              <div className="text-2xl font-black uppercase tracking-wider mb-6 text-[#ffff00]">
                Verified
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {SAMPLE_DATA.authenticity.authenticated &&
                <div className="relative bg-gradient-to-br from-[#10b981] to-[#059669] p-4 clip-corner">
                    <div className="text-white font-black text-xs uppercase">Authenticated</div>
                    <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-t-black border-l-[20px] border-l-transparent"></div>
                  </div>
                }
                {SAMPLE_DATA.authenticity.inPerson &&
                <div className="relative bg-gradient-to-br from-[#3b82f6] to-[#2563eb] p-4 clip-corner">
                    <div className="text-white font-black text-xs uppercase">In-Person</div>
                    <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-t-black border-l-[20px] border-l-transparent"></div>
                  </div>
                }
                {SAMPLE_DATA.authenticity.signed &&
                <div className="relative bg-gradient-to-br from-[#8b5cf6] to-[#7c3aed] p-4 clip-corner">
                    <div className="text-white font-black text-xs uppercase">Signed</div>
                    <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-t-black border-l-[20px] border-l-transparent"></div>
                  </div>
                }
                {SAMPLE_DATA.authenticity.certified &&
                <div className="relative bg-gradient-to-br from-[#f59e0b] to-[#d97706] p-4 clip-corner">
                    <div className="text-white font-black text-xs uppercase">Certified</div>
                    <div className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-t-black border-l-[20px] border-l-transparent"></div>
                  </div>
                }
              </div>

              {/* Certificate Details */}
              {SAMPLE_DATA.authenticity.certified &&
              <div className="bg-gray-900 border-l-4 border-[#ffff00] p-6 mt-6">
                  <div className="text-[#ffff00] font-black text-xs uppercase tracking-wider mb-3">
                    Certificate
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Authority:</span>
                      <span className="font-bold">{SAMPLE_DATA.authenticity.certifiedBy}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">ID:</span>
                      <span className="font-mono text-[#00ffff]">{SAMPLE_DATA.authenticity.certificateNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Date:</span>
                      <span>{SAMPLE_DATA.authenticity.certificationDate}</span>
                    </div>
                  </div>
                </div>
              }
            </div>

            {/* Markers Toggle */}
            <button
              onClick={() => setShowMarkers(!showMarkers)}
              className="w-full py-6 px-8 bg-gradient-to-r from-[#ff0080] to-[#ff0040] hover:from-[#ff0040] hover:to-[#ff0080] text-white font-black uppercase tracking-wider transition-all duration-300 shadow-[0_0_30px_#ff0080] hover:shadow-[0_0_50px_#ff0080]">
              
              {showMarkers ? '✓ Markers On' : 'Show Markers'}
            </button>
          </div>
        </div>

        {/* Content Grid - Magazine Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* About */}
          <div className="bg-gray-900 p-8 border-t-4 border-[#00ffff]">
            <h2 className="text-3xl font-black uppercase mb-6 text-[#00ffff]">
              The Story
            </h2>
            <p className="text-gray-300 leading-relaxed text-lg">
              {SAMPLE_DATA.description}
            </p>
          </div>

          {/* Signatures */}
          {SAMPLE_DATA.signedBy.length > 0 &&
          <div className="bg-gradient-to-br from-gray-900 to-black p-8 border-t-4 border-[#ffff00]">
              <h2 className="text-3xl font-black uppercase mb-6 text-[#ffff00]">
                Signed By
              </h2>
              <div className="space-y-4">
                {SAMPLE_DATA.signedBy.map((name, idx) =>
              <div key={idx} className="text-4xl font-black text-white uppercase tracking-tight">
                    {name}
                  </div>
              )}
              </div>
            </div>
          }

          {/* Collector Notes - Full Width */}
          <div className="md:col-span-2 bg-black border-4 border-[#ff0080] p-8">
            <h2 className="text-3xl font-black uppercase mb-6 text-[#ff0080]">
              Collector's Notes
            </h2>
            <p className="text-gray-300 leading-relaxed text-lg italic">
              “{SAMPLE_DATA.collectorNotes}”
            </p>
          </div>
        </div>
      </div>
    </div>);

}